import { SuperClusterAlgorithm } from "@googlemaps/markerclusterer";
import { Uuid } from "@legacy-megarax/common";
import { CustomerStatus, customerStatuses, TradeRouteSimple, VisitAddress } from "@legacy-megarax/crm-contracts";
import {
  BreadcrumbMarkers,
  PageTitleManager,
  qsIdSchema,
  QsInput,
  useNewRouterQsV2,
} from "@legacy-megarax/ui-components";
import { useCustomersResource } from "@megarax/crm-resource-hooks";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import _, { Dictionary } from "lodash";
import React from "react";
import { renderToString } from "react-dom/server";
import { useQuery } from "react-query";
import { z } from "zod";

import { useClientManager } from "@megaron/react-clients";

import { formatAddress, Google } from "../helpers";
import { customerHasAddress } from "../mapsCommon/customerHasAddress";
import { ExtendedMarkerClusterer } from "../mapsCommon/ExtendedMarkerClusterer";
import { googleMarkerFactory } from "../mapsCommon/markerFactoryV2";
import { notEmpty } from "../utils";
import { Filters } from "./Filters";
import { RouteMenu } from "./RouteMenu";
import { useRefCallback } from "./useRefCallback";
import { useDrawRoute } from "./useRouteDrawer";

interface Props {
  initialProps?: {
    tradeRoute: TradeRouteSimple;
    stopDurations: Dictionary<{
      durationMinutes: string;
    }>;
    route: TradeRouteCustomer[];
  };
  map: google.maps.Map;
  google: Google;
}

export const TradeRouteDetails: React.FC<Props> = ({ initialProps, map, google }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [route, setRoute] = React.useState<TradeRouteCustomer[]>(initialProps?.route ?? []);
  const [stopDurations, setStopDurations] = React.useState<
    Dictionary<{
      durationMinutes: string;
    }>
  >(initialProps?.stopDurations ?? {});
  const [travelData, setTravelData] = React.useState<TravelData[]>([]);
  const [isEdited, setIsEdited] = React.useState(false);

  const prevZoom = React.useRef<number | undefined>(undefined);
  const idleListener = React.useRef<google.maps.MapsEventListener>(undefined);
  const routeMarkers = React.useRef<google.maps.Marker[]>([]);
  const homeMarker = React.useRef<google.maps.Marker>(undefined);
  const infoWindow = React.useRef<google.maps.InfoWindow>(new google.maps.InfoWindow());

  const { debouncedDrawRoute } = useDrawRoute({ google, map, setTravelData });
  const [markerFactory] = React.useState(googleMarkerFactory({ google, map }));
  const [markerClusterer] = React.useState(
    new ExtendedMarkerClusterer({
      map,
      markers: [],
      algorithm: new SuperClusterAlgorithm({}),
      renderer: {
        render: ({ count, position }) => {
          return markerFactory.generate({
            position: { lat: position.lat(), lng: position.lng() },
            variant: "cluster",
            idx: 0,
            text: String(count),
          });
        },
      },
    }),
  );

  const { qs, setQs, setAllQs } = useNewRouterQsV2(tradeRouteQsSchema, {
    userId: initialProps?.tradeRoute?.userId ? initialProps.tradeRoute.userId.toString() : undefined,
    status: ["vip"],
  });

  const { listCustomers } = useCustomersResource();

  const addRouteStop = useRefCallback(
    (entry: TradeRouteCustomer) => {
      setStopDurations({ ...stopDurations, [entry.uuid]: { durationMinutes: "20" } });
      setRoute([...route, entry]);
    },
    [route, stopDurations],
  );

  const removeRouteStop = useRefCallback(
    (uuid: Uuid) => {
      setStopDurations({ ...stopDurations, [uuid]: { durationMinutes: "20" } });
      setRoute(route.filter((item) => item.uuid !== uuid));
    },
    [route, stopDurations],
  );

  const userProfileQuery = useClientManager("auth")
    .userProfilesQuery()
    .useQuery({ legacyUserIds: qs.userId ? [qs.userId] : [] }, { enabled: qs.userId !== undefined });

  const regionQuery = useClientManager("crm")
    .regionsQuery()
    .useQuery(
      { ownerEmail: userProfileQuery.data?.items[0].email },
      { enabled: userProfileQuery.data?.items[0].email !== undefined },
    );

  const region = regionQuery.data?.items[0];

  const { data: customers } = useQuery(
    ["customers", region, qs],
    () => {
      if (!region) return;
      return listCustomers({
        regionName: [region.name],
        limit: 1000,
        searchText: qs.searchText,
        status: qs.status ?? [],
        visitedIn: qs.visitedSince.value
          ? qs.visitedSince.bool
            ? qs.visitedSince.value
            : -qs.visitedSince.value
          : undefined,
        orderedIn: qs.orderedSince.value
          ? qs.orderedSince.bool
            ? qs.orderedSince.value
            : -qs.orderedSince.value
          : undefined,
        isOnRoute: qs.onRoute === "true" ? true : qs.onRoute === "false" ? false : undefined,
      }).map((res) => res.items);
    },
    { keepPreviousData: true, enabled: !!region },
  );

  const customerEntries = React.useMemo(() => {
    if (!customers) return undefined;
    if (customers.isFailure) return customers;
    return customers.map((res) =>
      res.filter(customerHasAddress).filter((val) => !route.find((stop) => stop.uuid === val.uuid)),
    );
  }, [customers, route]);

  const updateMarkers = React.useCallback(
    (fullRefresh: boolean) => {
      const skipUpdate = prevZoom.current === map.getZoom() && !fullRefresh;
      if (!customerEntries || !customerEntries.isOk || skipUpdate) return;
      prevZoom.current = map.getZoom();

      const markers = customerEntries.value
        .map((entry, idx) => {
          if (!entry.status) return null;

          const position = { lat: entry.visitAddress.lat.toNumber(), lng: entry.visitAddress.lng.toNumber() };

          const marker = markerFactory.generate({
            position,
            variant: entry.status,
            idx,
            text: entry.name ?? "",
          });
          marker.addListener("click", () => {
            map.panTo(position);
            infoWindow.current.setContent(
              renderToString(
                <InfoBox
                  onRoute={false}
                  name={entry.name ?? ""}
                  uuid={entry.uuid}
                  address={formatAddress({
                    locality: entry.visitAddress.locality,
                    street: entry.visitAddress.street,
                    postalCode: "",
                    country: "",
                    name: entry.visitAddress.name,
                  })}
                />,
              ),
            );
            google.maps.event.clearListeners(infoWindow.current, "domready");
            google.maps.event.addListener(infoWindow.current, "domready", () => {
              document
                .getElementById("infowindow-button")
                ?.addEventListener("click", () => addRouteStop.current({ ...entry, name: entry.name ?? "" }));
            });
            infoWindow.current.open({
              anchor: marker,
              map,
              shouldFocus: false,
            });
          });

          return marker;
        })
        .filter(notEmpty);

      markerClusterer.clearMarkers();
      markerClusterer.addMarkers(markers);
    },
    [customerEntries],
  );

  React.useEffect(() => {
    updateMarkers(true);
    markerClusterer.updateVisibility();

    if (idleListener.current) google.maps.event.removeListener(idleListener.current);
    idleListener.current = map.addListener("idle", () => {
      updateMarkers(false);
      markerClusterer.updateVisibility();
    });
  }, [updateMarkers]);

  const updateRouteMarkers = React.useCallback(() => {
    const markers = route
      .map((entry, idx) => {
        if (!entry.status) return null;

        const position = { lat: entry.visitAddress.lat.toNumber(), lng: entry.visitAddress.lng.toNumber() };

        const marker = markerFactory.generate({
          position,
          variant: "route",
          idx,
          text: entry.name,
        });
        marker.addListener("click", () => {
          map.panTo(position);

          infoWindow.current.setContent(
            renderToString(
              <InfoBox
                onRoute={true}
                name={entry.name ?? ""}
                uuid={entry.uuid}
                address={formatAddress({
                  locality: entry.visitAddress.locality,
                  street: entry.visitAddress.street,
                  postalCode: "",
                  country: "",
                  name: entry.visitAddress.name,
                })}
              />,
            ),
          );
          google.maps.event.clearListeners(infoWindow.current, "domready");
          google.maps.event.addListener(infoWindow.current, "domready", () => {
            document
              .getElementById("infowindow-button")
              ?.addEventListener("click", () => removeRouteStop.current(entry.uuid));
          });
          infoWindow.current.open({
            anchor: marker,
            map,
            shouldFocus: false,
          });
        });

        return marker;
      })
      .filter(notEmpty);

    routeMarkers.current.forEach((marker) => marker.setMap(null));
    routeMarkers.current = markers;
  }, [route]);

  React.useEffect(() => {
    updateRouteMarkers();
  }, [updateRouteMarkers]);

  React.useEffect(() => {
    map.addListener("zoom_changed", () => {
      markerClusterer.clearMarkers();
    });
  }, []);

  React.useEffect(() => {
    const salespersonHomePosition =
      region?.homeLatitude && region?.homeLongitude
        ? {
            lat: region.homeLatitude.toNumber(),
            lng: region.homeLongitude.toNumber(),
          }
        : null;

    debouncedDrawRoute(route, salespersonHomePosition);
  }, [route, region?.homeLatitude, region?.homeLongitude]);

  React.useEffect(() => {
    const salespersonHomePosition =
      region?.homeLatitude && region?.homeLongitude
        ? {
            lat: region.homeLatitude.toNumber(),
            lng: region.homeLongitude.toNumber(),
          }
        : null;

    if (homeMarker.current) homeMarker.current.setMap(null);
    if (salespersonHomePosition) {
      const marker = markerFactory.generate({
        position: salespersonHomePosition,
        variant: "home",
        idx: 0,
        text: "",
      });
      homeMarker.current = marker;
    }
  }, [region?.homeLatitude, region?.homeLongitude]);

  const revertToInitialRoute = () => {
    if (!initialProps) {
      setQs("userId", undefined);
      setRoute([]);
      setStopDurations({});
      return;
    }
    setQs("userId", initialProps.tradeRoute.userId);
    setRoute(initialProps.route);
    setStopDurations(initialProps.stopDurations);
  };

  React.useEffect(() => {
    if (!initialProps) return setIsEdited(true);
    const tradeRouteString =
      route.map((entry) => entry.uuid + stopDurations[entry.uuid].durationMinutes).join("") + qs.userId;
    const initialTradeRouteString =
      initialProps.route.map((entry) => entry.uuid + initialProps.stopDurations[entry.uuid].durationMinutes).join("") +
      initialProps.tradeRoute.userId;
    setIsEdited(tradeRouteString !== initialTradeRouteString);
  }, [route, qs.userId, stopDurations]);

  return (
    <>
      <PageTitleManager title={initialProps?.tradeRoute.name ?? "Trasa"} />
      <BreadcrumbMarkers
        breadcrumbs={[
          { title: "Trasy", id: "trasy" },
          { title: initialProps?.tradeRoute.name ?? "Trasa", id: "trasa" },
        ]}
      />

      <Filters
        isMobile={isMobile}
        qs={qs}
        setQueryFilters={setAllQs}
        isEdited={isEdited}
        revertToInitialRoute={revertToInitialRoute}
      />
      <RouteMenu
        initialTradeRoute={initialProps?.tradeRoute ?? null}
        homeSelected={!!region}
        isMobile={isMobile}
        travelData={travelData}
        stopDurations={stopDurations}
        setStopDurations={setStopDurations}
        route={route}
        setRoute={setRoute}
        userId={qs.userId}
        qs={qs}
        setQs={setQs}
        isEdited={isEdited}
      />
    </>
  );
};

const InfoBox: React.FC<{ name: string; address: string; uuid: Uuid; onRoute: boolean }> = ({
  name,
  uuid,
  address,
  onRoute,
}) => {
  const buttonStyle = {
    width: "100%",
    marginTop: "8px",
    border: "none",
    cursor: "pointer",
    padding: "4px",
  };
  return (
    <Box display="flex" justifyContent="center" flexDirection="column" alignItems="center">
      <a href={`/crm/customers/id/${uuid}`}>
        <Typography>{name}</Typography>
      </a>
      <Typography variant="caption">{address}</Typography>
      {onRoute ? (
        <button style={buttonStyle} id={"infowindow-button"}>
          Usuń z trasy
        </button>
      ) : (
        <button style={buttonStyle} id={"infowindow-button"}>
          Dodaj do trasy
        </button>
      )}
    </Box>
  );
};

export type TradeRouteCustomer = {
  uuid: Uuid;
  name: string;
  visitAddress: VisitAddress;
  permanentTradeRoutesCount: number | null;
  status: CustomerStatus | null;
};

export interface TravelData {
  duration: number;
  distance: number;
}

export interface QueryFilters {
  userId: number | undefined;
  searchText: string;
  visitedSince: { value: number | null; bool: boolean };
  orderedSince: { value: number | null; bool: boolean };
  status: CustomerStatus[] | null;
  onRoute: "true" | "false" | null;
}
export type SetQueryFilters = (key: keyof QueryFilters, value: QueryFilters[typeof key]) => void;

const tradeRouteQsSchema: QsInput<QueryFilters> = {
  userId: qsIdSchema,
  searchText: {
    validator: z.string(),
    fallback: "",
  },
  visitedSince: {
    validator: z.preprocess(
      (obj: any) => ({
        value:
          obj?.value === null
            ? null
            : typeof obj?.value === "string" && !isNaN(parseInt(obj?.value))
            ? _.toNumber(obj?.value)
            : null,
        bool:
          typeof obj?.bool === "string" ? (obj?.bool === "true" ? true : obj?.bool === "false" ? false : true) : true,
      }),
      z.object({ value: z.number().nullable(), bool: z.boolean() }),
    ),
    fallback: { value: null, bool: true },
  },
  orderedSince: {
    validator: z.preprocess(
      (obj: any) => ({
        value:
          obj?.value === null
            ? null
            : typeof obj?.value === "string" && !isNaN(parseInt(obj?.value))
            ? _.toNumber(obj?.value)
            : null,
        bool:
          typeof obj?.bool === "string" ? (obj?.bool === "true" ? true : obj?.bool === "false" ? false : true) : true,
      }),
      z.object({ value: z.number().nullable(), bool: z.boolean() }),
    ),
    fallback: { value: null, bool: true },
  },
  status: {
    validator: z.array(z.enum(customerStatuses)).nullable(),
    fallback: null,
  },
  onRoute: {
    validator: z.enum(["true", "false"]).nullable(),
    fallback: null,
  },
};
